import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  Autocomplete,
  InputLabel,
  FormControl,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState, useEffect } from "react";
import Header from "../../../components/Header";
import {
  accountsmanagement,
  caseManagementUrl,
  clientManagementUrl,
} from "../../../config";
import {
  allCorporateClients,
  allIndividualClients,
  allCasesView,
  currency,
  FeeNoteDocumentsTypes,
  ProformaFeeNotesQuotationsById,
} from "../../../data/Axios/queries";
import {
  fetchDataEngine,
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../data/Axios/DynamicService";

const base_url = accountsmanagement.uri;
const client_base_url = clientManagementUrl.uri;
const case_base_url = caseManagementUrl.uri;

const clientTypeOptions = [
  { value: 1, label: "Individual" },
  { value: 2, label: "Corporate" },
];

const FeeNoteForm = (props) => {
  const { isEditing = false, data = {}, onClose, refetchData, initialData } = props;

  const [initialValues, setInitialValues] = useState({
    id: null,
    clientTypeId: "",
    documentTypeId: "",
    clientId: "",
    caseId: "",
    proformaFeeNoteQuotationId: null,
    dueDate: new Date().toISOString().split("T")[0],
    issueDate: new Date().toISOString().split("T")[0],
    currencyId: "",
    exchangeRate: 1.0,
    amount: 0,
  });

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id) || null;

  const [documentTypes, setDocumentTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [cases, setCases] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [allIndividualClientsData, setAllIndividualClientsData] = useState([]);
  const [allCorporateClientsData, setAllCorporateClientsData] = useState([]);
  const [allCasesData, setAllCasesData] = useState([]);
  const [proformaFeeNotes, setProformaFeeNotes] = useState([]);


  // 🧠 Helper: filter clients by type
  const filterClients = (clientTypeId, individuals, corporates) => {
    const typeId = parseInt(clientTypeId);
    if (typeId === 1) {
      return individuals.map((c) => ({ id: c.id, name: c.fullName }));
    } else if (typeId === 2) {
      return corporates.map((c) => ({ id: c.id, name: c.companyName }));
    }
    return [];
  };

  // 🧠 Fetch master data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const docTypeData = await graphqlQuery(FeeNoteDocumentsTypes, base_url);
        const filteredDocTypes = (docTypeData || []).filter(
          (doc) => [3, 4, 5, 6].includes(doc.id)
        );
        setDocumentTypes(filteredDocTypes);

        const individualData = await fetchDataEngine(
          "allIndividualClients",
          "clientModel",
          { isActive: 1 },
          ["id", "fullName"],
          client_base_url
        );
        const corporatesData = await fetchDataEngine(
          "allCorporateClients",
          "clientModel",
          { isActive: 1 },
          ["id", "companyName"],
          client_base_url
        );
        setAllIndividualClientsData(individualData || []);
        setAllCorporateClientsData(corporatesData || []);

        const caseData = await graphqlQuery(allCasesView, case_base_url);
        setAllCasesData(caseData || []);

        const currencyData = await graphqlQuery(currency, base_url);
        setCurrencies(currencyData || []);



      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // 🧠 Fetch Proforma Fee Notes only when document type = 'Proforma Fee Note'
  useEffect(() => {
    const selectedDoc = documentTypes.find(
      (d) => d.id === initialValues.documentTypeId
    );

    if (selectedDoc?.documentTypeName === "Proforma Fee Note") {
      const fetchProformaFeeNotes = async () => {
        try {
          const proformaResponse = await fetchDataEngine(
            "proformaFeeNotesQuotationsById",
            "proformaFeeNotesQuotationsModel",
            {
              id: 0,
              isActive: 1,
            },
            ["id", "clientName", "caseNumber", "amount"],
            base_url
          );

          setProformaFeeNotes(proformaResponse || []);
        } catch (error) {
          console.error("Error fetching proforma fee notes:", error);
        }
      };

      fetchProformaFeeNotes();
    } else {
      setProformaFeeNotes([]); // clear dropdown when not needed
    }
  }, [documentTypes, initialValues.documentTypeId]);


  // 🧠 Map backend fields -> form fields when editing
  useEffect(() => {
    if (data && isEditing) {
      const mappedData = {
        id: data.id || null,
        clientTypeId: data.clientTypeId || "",
        documentTypeId: data.documentTypeId || "",
        clientId: data.clientId || "",
        caseId: data.caseId || "",
        proformaFeeNoteQuotationId: data.proformaFeeNoteQuotationId || null,
        amount: data.amount || 0,
        currencyId: data.currencyId || "",
        exchangeRate: data.exchangeRate || 1.0,
        issueDate: data.issueDate || new Date().toISOString().split("T")[0],
        dueDate: data.dueDate || new Date().toISOString().split("T")[0],
      };
      setInitialValues(mappedData);

      if (data.clientTypeId) {
        const filteredClients = filterClients(
          data.clientTypeId,
          allIndividualClientsData,
          allCorporateClientsData
        );
        setClients(filteredClients);

        const relatedCases = allCasesData.filter(
          (c) =>
            c.clientId === data.clientId &&
            c.clientTypeId === parseInt(data.clientTypeId)
        );
        setCases(relatedCases);
      }
    }
  }, [data, isEditing, allIndividualClientsData, allCorporateClientsData, allCasesData]);

  const validationSchema = yup.object().shape({
    clientTypeId: yup.string().required("Client Type is required"),
    documentTypeId: yup.string().required("Document Type is required"),
    clientId: yup.string().required("Client is required"),
    caseId: yup.string().required("Case is required"),
    currencyId: yup.string().required("Currency is required"),
    exchangeRate: yup
      .number()
      .min(0.01, "Rate must be greater than 0")
      .required("Exchange Rate is required"),
    dueDate: yup.date().required("Due Date is required"),
    issueDate: yup.date().required("Issue Date is required"),
    amount: yup.number().min(0, "Amount must be positive").required("Amount is required"),
  });

  const isNonMobile = useMediaQuery("(min-width:600px)");

  // 🧠 Submit handler
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const mutationData = {
        ...values,
        clientTypeId: parseInt(values.clientTypeId),
        documentTypeId: parseInt(values.documentTypeId),
        clientId: parseInt(values.clientId),
        caseId: parseInt(values.caseId),
        currencyId: parseInt(values.currencyId),
        exchangeRate: parseFloat(values.exchangeRate),
        amount: parseFloat(values.amount),
        proformaFeeNoteQuotationId: values.proformaFeeNoteQuotationId
          ? parseInt(values.proformaFeeNoteQuotationId)
          : null,
        isActive: 1,
        issueDate: `${values.issueDate}T12:00:00`,
        dueDate: `${values.dueDate}T12:00:00`,
      };


      const companyId = parseInt(decodedToken.CompanyId) || null;
      const finalMutationData = isEditing
        ? {
          id: data?.id,
          ...mutationData,
          companyId: companyId,
          isActive: 1,
          updatedBy: userId,
          statusId: data?.statusId,
          createdBy: data?.createdBy
        }
        : { ...mutationData, createdBy: userId, companyId: companyId, isActive: 1, statusId: 1 };

      // const mutationName = isEditing ? "updateFeeNotes" : "createFeeNotes";
      // const responseKey = isEditing ? "feeNotes" : "feeNotes";

      // const argName =  "feeNotes";

      const response = await generateAndExecuteMutation(
        isEditing ? "updateFeeNotes" : "createFeeNotes",
        finalMutationData,
        "feeNotes",
        base_url
      );


      if (response) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Fee Note ${isEditing ? "updated" : "created"} successfully.`,
        })
          if (refetchData) refetchData();
          if (onClose) onClose();
        
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Something went wrong while saving the fee note.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header
        title={isEditing ? "Edit Fee Note" : "Create Fee Note"}
        subtitle={isEditing ? "Edit existing Fee Note" : "Create new Fee Note"}
      />

      <Formik
        onSubmit={handleSubmit}
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="25px"
              gridTemplateColumns="repeat(2, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >

              {/* Document Type */}
              <FormControl fullWidth variant="filled">
                <InputLabel>Document Type *</InputLabel>
                <Select
                  name="documentTypeId"
                  value={values.documentTypeId}
                  onChange={handleChange}
                >
                  {documentTypes.map((doc) => (
                    <MenuItem key={doc.id} value={doc.id}>
                      {doc.documentTypeName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {/* Client Type */}
              <FormControl fullWidth variant="filled">
                <InputLabel>Client Type *</InputLabel>
                <Select
                  name="clientTypeId"
                  value={values.clientTypeId}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setFieldValue("clientTypeId", newType);
                    setFieldValue("clientId", "");
                    setClients(
                      filterClients(
                        newType,
                        allIndividualClientsData,
                        allCorporateClientsData
                      )
                    );
                  }}
                >
                  {clientTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>


              {/* Client */}
              <Autocomplete
                fullWidth
                options={clients}
                getOptionLabel={(o) => o.name || ""}
                value={clients.find((c) => c.id === values.clientId) || null}
                onChange={(e, newVal) => {
                  setFieldValue("clientId", newVal?.id || "");
                  setCases(
                    allCasesData.filter(
                      (c) =>
                        c.clientId === newVal?.id &&
                        c.clientTypeId === parseInt(values.clientTypeId)
                    )
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    label="Client *"
                    error={touched.clientId && !!errors.clientId}
                    helperText={touched.clientId && errors.clientId}
                  />
                )}
              />

              {/* Case */}
              <Autocomplete
                fullWidth
                options={cases}
                getOptionLabel={(o) => o.caseNumber || o.title || o.id.toString()}
                value={cases.find((c) => c.id === values.caseId) || null}
                onChange={(e, newVal) =>
                  setFieldValue("caseId", newVal?.id || "")
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="filled"
                    label="Case *"
                    error={touched.caseId && !!errors.caseId}
                    helperText={touched.caseId && errors.caseId}
                  />
                )}
              />

               {/* Proforma Fee Note ID (only visible when Document Type = 'Proforma Fee Note') */}
              {(() => {
                const selectedDoc = documentTypes.find(
                  (d) => d.id === values.documentTypeId
                );

                if (selectedDoc?.documentTypeName === "Proforma Fee Note") {
                  return (
                    <FormControl fullWidth variant="filled">
                      <InputLabel>Proforma Fee Note *</InputLabel>
                      <Select
                        name="proformaFeeNoteQuotationId"
                        value={values.proformaFeeNoteQuotationId || ""}
                        onChange={handleChange}
                      >
                        {proformaFeeNotes.map((note) => (
                          <MenuItem key={note.id} value={note.id}>
                            {note.clientName
                              ? `${note.clientName} (${note.caseNumber || "No Case"})`
                              : `Proforma #${note.id}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }
                return null;
              })()}


              {/* Proforma (optional)
              <TextField
                fullWidth
                variant="filled"
                label="Proforma Quotation ID (optional)"
                name="proformaFeeNoteQuotationId"
                type="number"
                value={values.proformaFeeNoteQuotationId || ""}
                onChange={handleChange}
              /> */}

              {/* Amount
              <TextField
                fullWidth
                variant="filled"
                label="Amount *"
                name="amount"
                type="number"
                value={values.amount}
                onChange={handleChange}
                error={touched.amount && !!errors.amount}
                helperText={touched.amount && errors.amount}
              /> */}

              {/* Currency */}
              <FormControl fullWidth variant="filled">
                <InputLabel>Currency *</InputLabel>
                <Select
                  name="currencyId"
                  value={values.currencyId}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    setFieldValue("currencyId", selectedId);

                    // Find selected currency
                    const selectedCurrency = currencies.find((c) => c.id === selectedId);

                    // Automatically update exchange rate if it exists
                    if (selectedCurrency && selectedCurrency.exchangeRate) {
                      setFieldValue("exchangeRate", selectedCurrency.exchangeRate);
                    }
                  }}
                >
                  {currencies.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.currencyName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Exchange Rate (readonly) */}
              <TextField
                fullWidth
                variant="filled"
                label="Exchange Rate"
                name="exchangeRate"
                type="number"
                value={values.exchangeRate}
                InputProps={{ readOnly: true }}
              />


              {/* Issue Date */}
              <TextField
                fullWidth
                type="date"
                variant="filled"
                label="Issue Date *"
                name="issueDate"
                InputLabelProps={{ shrink: true }}
                value={values.issueDate}
                onChange={handleChange}
              />

              {/* Due Date */}
              <TextField
                fullWidth
                type="date"
                variant="filled"
                label="Due Date *"
                name="dueDate"
                InputLabelProps={{ shrink: true }}
                value={values.dueDate}
                onChange={handleChange}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? "Submitting..."
                  : isEditing
                    ? "Update"
                    : "Create"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default FeeNoteForm;
